import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const empty = { code: "", kind: "percent", value: 10, min_order: 0, active: true };

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/coupons").then((r) => setCoupons(r.data));

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/coupons", {
        code: form.code.trim().toUpperCase(),
        kind: form.kind,
        value: Number(form.value),
        min_order: Number(form.min_order) || 0,
        active: form.active,
      });
      toast.success("Coupon created");
      setForm(empty);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete coupon?")) return;
    try {
      await api.delete(`/coupons/${id}`);
      toast.success("Coupon deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <AdminLayout title="Coupons">
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <form onSubmit={create} className="card p-5 space-y-4" data-testid="admin-coupon-form">
            <div className="font-display text-lg font-semibold">New coupon</div>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6b6b6b]">Code</span>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                required
                placeholder="WELCOME10"
                className="mt-1 w-full h-10 bg-white border border-[#e0d8c8] rounded-md outline-none px-3 text-sm uppercase"
                data-testid="coupon-code-input"
              />
            </label>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6b6b6b]">Type</span>
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                className="mt-1 w-full h-10 bg-white border border-[#e0d8c8] rounded-md outline-none px-3 text-sm"
                data-testid="coupon-kind-select"
              >
                <option value="percent">Percent off</option>
                <option value="flat">Flat amount off (₹)</option>
              </select>
            </label>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6b6b6b]">
                {form.kind === "percent" ? "Discount %" : "Discount amount (₹)"}
              </span>
              <input
                type="number"
                min="0"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                required
                className="mt-1 w-full h-10 bg-white border border-[#e0d8c8] rounded-md outline-none px-3 text-sm"
                data-testid="coupon-value-input"
              />
            </label>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6b6b6b]">Minimum order (₹)</span>
              <input
                type="number"
                min="0"
                value={form.min_order}
                onChange={(e) => setForm({ ...form, min_order: e.target.value })}
                className="mt-1 w-full h-10 bg-white border border-[#e0d8c8] rounded-md outline-none px-3 text-sm"
                data-testid="coupon-min-input"
              />
            </label>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="w-4 h-4 accent-[#c8532e]"
                data-testid="coupon-active-checkbox"
              />
              Active
            </label>

            <button type="submit" disabled={busy} className="btn-primary w-full text-sm" data-testid="coupon-submit">
              <Plus className="w-4 h-4 inline -mt-0.5 mr-1" /> {busy ? "Creating…" : "Create coupon"}
            </button>
          </form>
        </div>

        <div className="md:col-span-2">
          <div className="card overflow-hidden" data-testid="admin-coupons-table">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#e0d8c8] text-xs uppercase tracking-[0.16em] text-[#6b6b6b]">
              <div className="col-span-3">Code</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-2">Value</div>
              <div className="col-span-2">Min. Order</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1 text-right">×</div>
            </div>
            {coupons.length === 0 && (
              <div className="p-6 text-sm text-[#6b6b6b]">No coupons yet. Create your first one.</div>
            )}
            {coupons.map((c) => (
              <div key={c.id} className="grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-[#e0d8c8]" data-testid={`admin-coupon-row-${c.id}`}>
                <div className="col-span-3 font-mono font-semibold">{c.code}</div>
                <div className="col-span-2 capitalize text-sm">{c.kind}</div>
                <div className="col-span-2 text-sm">
                  {c.kind === "percent" ? `${c.value}%` : formatINR(c.value)}
                </div>
                <div className="col-span-2 text-sm">{formatINR(c.min_order)}</div>
                <div className="col-span-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${c.active ? "bg-[#3a6a3a] text-white" : "bg-[#e0d8c8] text-[#6b6b6b]"}`}>
                    {c.active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="col-span-1 text-right">
                  <button onClick={() => remove(c.id)} className="w-8 h-8 border border-[#e0d8c8] rounded-md flex items-center justify-center hover:bg-[#faf7f2] ml-auto" data-testid={`coupon-delete-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
